/**
 * Utility functions for transforming dashboard data
 * Centralizes data transformation logic for better maintainability
 */

import { getTimeAgo, formatCurrency } from './helpers'

/**
 * Get color class for update index
 * @param {number} index - Index of the update
 * @returns {string} Tailwind border color class
 */
export const getColorForIndex = (index) => {
  const colors = ['border-l-blue-500', 'border-l-green-500', 'border-l-purple-500', 'border-l-yellow-500', 'border-l-red-500']
  return colors[index % colors.length]
}

/**
 * Get status color for follow-on opportunities
 * @param {string} status - Status string
 * @returns {string} Tailwind background color class
 */
export const getStatusColor = (status) => {
  if (!status) return 'bg-gray-400'
  const normalized = String(status).toLowerCase()
  if (normalized.includes('active')) return 'bg-green-500'
  if (normalized.includes('available') || normalized.includes('open')) return 'bg-blue-500'
  if (normalized.includes('pending')) return 'bg-yellow-500'
  if (normalized.includes('closed')) return 'bg-gray-500'
  return 'bg-blue-400'
}

/**
 * Transform updates API data to match UI structure
 * @param {Object} updatesData - Raw updates data from API
 * @returns {Array} Transformed updates array
 */
export const transformUpdates = (updatesData) => {
  const items = updatesData?.results || []
  
  return items.map((update, index) => ({
    id: update.id,
    description: update.subject,
    timeAgo: getTimeAgo(update.date),
    color: getColorForIndex(index),
    companyName: update.companyName,
    startupId: update.startupId,
    date: update.date
  }))
}

/**
 * Transform follow-on opportunities API data to match UI structure
 * @param {Object} followOnsData - Raw follow-ons data from API
 * @returns {Array} Transformed follow-ons array
 */
export const transformFollowOns = (followOnsData) => {
  const items = followOnsData?.results || []
  
  return items.map(item => ({
    company: item.companyName,
    status: item.status || 'Open',
    statusColor: getStatusColor(item.status),
    dealSize: formatCurrency(item.dealSize || '—'),
    startupId: item.startupId,
    id: item.id,
    interestFormUrl: item.interestFormUrl,
  }))
}

/**
 * Calculate total investments from timeline data
 * @param {Array} timelineData - Array of investment objects
 * @returns {number} Total investment amount
 */
export const calculateTotalInvestments = (timelineData) => {
  return timelineData.reduce((acc, investment) => {
    const amount = parseFloat(investment.investmentAmount || 0)
    return acc + amount
  }, 0)
}
